import { useState } from 'react';

import { Button } from '@frontend/components/ui/button';
import { AnswerEditor } from '@frontend/shared/components/AnswerEditor';
import { CategorySelect } from '@frontend/shared/components/CategorySelect/CategorySelect';
import { EditorButtons } from '@frontend/shared/components/Editor/Buttons/EditorButtons';
import { useSharedEditor } from '@frontend/shared/components/Editor/useSharedEditor';
import { useSaver } from '@frontend/shared/components/Question/hooks/Question.hooks.useSaver';
import { QuestionContext } from '@frontend/shared/components/Question/QuestionContext';
import { TitleEditor } from '@frontend/shared/components/TitleEditor';
import { useIsEditor } from '@frontend/shared/hooks/shared.hook.useIsEditor';
import { useRequiredContext } from '@frontend/shared/hooks/useRequiredContext';

export function QuestionEdit({
  setEditMode,
}: {
  setEditMode: (editMode: boolean) => void;
}) {
  const question = useRequiredContext(QuestionContext);
  const isEditor = useIsEditor();
  const { editor } = useSharedEditor({
    editable: true,
    content: question.answer_json
      ? JSON.parse(question.answer_json)
      : undefined,
    autofocus: true,
  });
  const [title, setTitle] = useState(question?.title || '');
  const [categoryId, setCategoryId] = useState(question?.category_id || '');
  const saver = useSaver(question, title, editor, categoryId, () =>
    setEditMode(false),
  );

  // Only allow editors to access the edit interface
  if (!isEditor) {
    return null;
  }

  return (
    <div className="px-4 py-2">
      <TitleEditor title={title} setTitle={setTitle} className="pb-2 pt-2" />
      <AnswerEditor editor={editor} className="mt-1" />
      <div className="flex mt-2 py-2 gap-2 items-center">
        {editor && <EditorButtons editor={editor} />}
        <CategorySelect
          value={categoryId}
          onChange={setCategoryId}
          isRequired
          label=""
          maxW="240px"
        />
        <Button
          disabled={!question}
          size="sm"
          onClick={() => setEditMode(false)}
        >
          Cancel
        </Button>
        <Button
          size="sm"
          disabled={!question || saver.isSubmitting}
          onClick={saver.submit}
          className="font-semibold text-[13px] leading-4 px-4 bg-blue-600 hover:bg-blue-700 text-white"
        >
          {saver.isSubmitting ? 'Saving...' : 'Save & Verify'}
        </Button>
      </div>
    </div>
  );
}
